import { memo } from "react";
import { useSpaceIdentity } from "@/api/hooks";
import { useAppSelector } from "@/state/redux/hooks";
import { selectMe } from "@/state/redux/slices/ui";
import { Button } from "../button";
import { HeaderText } from "./Header";
import UserAvatar from "./UserAvatar";
import { useCollectionsSidebarToggle } from "./useSidebarToggle";

/**
 * The header's one collections control: whose space this is, and the way into
 * it.
 *
 * It used to carry the route's title — the collection's name on a collection
 * page. That name now heads the list itself, in `RecordsBand`, where it sits
 * against the records it names instead of two components away from them. What
 * is left here is the identity, which is the one thing true of every route and
 * the one thing that says what the sidebar behind the button will contain.
 *
 * The identity is the space being browsed, not the signed-in user: in someone
 * else's space the button opens *their* collections, and your own account
 * already has a home in that sidebar's footer.
 */
function CurrentPageWidget() {
  const { open, toggle } = useCollectionsSidebarToggle();
  const me = useAppSelector(selectMe);
  const { name, picture } = useSpaceIdentity();

  // Signed out there is no one to name, and the initials fallback would put a
  // bare "?" on the button. What the press does there is still worth saying.
  const label = me ? name || "You" : "Sign in";
  const showIdentity = !!(name || picture);

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={toggle}
      title={`${open ? "Hide" : "Show"} collections (⌘J)`}
      // `shrink` overrides the variant's shrink-0: a long name gives way before
      // it pushes the interval off the other end.
      className="min-w-0 shrink gap-2"
    >
      <HeaderText>{label}</HeaderText>
      {showIdentity && (
        <UserAvatar name={name} picture={picture} className="size-5 shrink-0" />
      )}
    </Button>
  );
}

export default memo(CurrentPageWidget);
